import React from 'react';
import styles from './EmotionChart.module.css';

const EmotionChart = ({ emotions, title }) => {
  if (!emotions || emotions.length === 0) {
    return (
      <div className={styles.chartContainer}>
        <h3>{title}</h3>
        <p className={styles.noData}>No emotion data available</p>
      </div>
    );
  }

  // Group emotions by name and sum frequencies
  const groupedEmotions = emotions.reduce((acc, emotion) => {
    const existing = acc.find(e => e.emotion === emotion.emotion);
    if (existing) {
      existing.frequency += emotion.frequency;
      existing.averageStrength = (existing.averageStrength + emotion.averageStrength) / 2;
      existing.totalOccurrences += emotion.totalOccurrences;
    } else {
      acc.push({ ...emotion });
    }
    return acc;
  }, []);

  const maxFreq = Math.max(...groupedEmotions.map(e => e.frequency));

  // Color palette for emotions
  const emotionColors = {
    // Positive emotions - use greens and blues
    'autonomy': 'var(--brand-as-blue-600)',
    'curiosity': 'var(--brand-as-green-500)',
    'playfulness': 'var(--brand-as-purple-400)',
    'mindfulness': 'var(--brand-as-blue-500)',
    'anticipation': 'var(--brand-as-blue-400)',
    'relief': 'var(--brand-as-green-400)',
    'comfort': 'var(--brand-as-green-600)',
    'openness': 'var(--brand-as-blue-300)',
    'relaxation': 'var(--brand-as-green-500)',
    'enthusiasm': 'var(--brand-as-purple-300)',
    'joy': 'var(--brand-as-purple-400)',
    'enjoyment': 'var(--brand-as-purple-500)',
    'self-control': 'var(--brand-as-green-700)',
    'satisfaction': 'var(--brand-as-green-500)',
    'seasonal_delight': 'var(--brand-as-blue-200)',
    
    // Neutral/contemplative emotions - use neutrals and browns
    'habit': 'var(--brand-as-brown-500)',
    'skepticism': 'var(--brand-as-neutral-600)',
    'quality_consciousness': 'var(--brand-as-neutral-700)',
    'clarity_of_preference': 'var(--brand-as-neutral-800)',
    'desire_for_authenticity': 'var(--brand-as-neutral-700)',
    'boredom': 'var(--brand-as-neutral-500)',
    'desire_for_novelty': 'var(--brand-as-purple-600)',
    
    // Mild negative emotions - use yellows/oranges
    'uncertainty': 'var(--brand-as-brown-400)',
    'worry': 'var(--brand-as-brown-600)',
    'spontaneity': 'var(--brand-as-pink-400)',
    'indulgence': 'var(--brand-as-pink-500)',
    'mild_frustration': 'var(--brand-as-pink-600)',
    
    // Strong negative emotions - use reds/pinks
    'embarrassment': 'var(--brand-as-pink-600)',
    'frustration': 'var(--brand-as-pink-700)',
    'distress': 'var(--brand-as-pink-800)',
    'stress': 'var(--brand-as-pink-700)',
    'dislike': 'var(--brand-as-pink-600)',
    
    // Action/energy emotions - use vibrant colors
    'desire_for_refreshment': 'var(--brand-as-blue-400)',
    'craving_freedom': 'var(--brand-as-blue-600)'
  };

  return (
    <div className={styles.chartContainer}>
      <h3>{title}</h3>
      <div className={styles.chart}>
        {groupedEmotions
          .sort((a, b) => b.frequency - a.frequency)
          .slice(0, 10) // Top 10 emotions
          .map((emotion, index) => (
            <div key={index} className={styles.emotionContainer}>
              <div className={styles.emotionBar}>
                <div 
                  className={styles.bar}
                  style={{ 
                    width: `${(emotion.frequency / maxFreq) * 100}%`,
                    backgroundColor: emotionColors[emotion.emotion] || 'var(--brand-as-blue-900)'
                  }}
                />
              </div>
              <div className={styles.emotionInfo}>
                <div className={styles.emotionName}>{emotion.emotion}</div>
                <div className={styles.emotionStats}>
                  <span className={styles.frequency}>{(emotion.frequency * 100).toFixed(1)}%</span>
                  <span className={styles.strength}>Strength: {emotion.averageStrength.toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EmotionChart;
